import { INSTAGRAM_COUNTRIES } from '@/config/constants';
import { SiSemrush } from 'react-icons/si';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const d = (dbDate, monthsAgo = 0) =>
    dayjs(dbDate).subtract(monthsAgo, 'month').format('MMM YYYY');

const dWeeks = (dbDate, weeksAgo = 0) =>
    dayjs(dbDate).subtract(weeksAgo, 'week').format('MMM D, YYYY');

// Puts "AI" on the analysis, so it never reads as AI-written comments.
const sentimentSource = (dbDate, country, byAi) =>
    `TikTok ${country} top post comments, ${dWeeks(dbDate, 1)} – ${dWeeks(dbDate)} · ${byAi}`;

const LAST_YEAR = 12;
const LAST_MONTH = 1;
const LAST_MONTH_LAST_YEAR = LAST_MONTH + LAST_YEAR;
const THREE_YEARS_AGO = 3 * LAST_YEAR;
const TWO_YEARS_AGO = 2 * LAST_YEAR;
const THREE_YEARS_AGO_WEEKS = 3 * 52;

/**
 * Market names as they read inside a sentence — not the geo table's, which
 * stores KR as "Korea, Republic of" and has no "in" for GLOBAL.
 */
const MARKET_PHRASES = {
    US: 'in the United States',
    GB: 'in the United Kingdom',
    FR: 'in France',
    JP: 'in Japan',
    KR: 'in South Korea',
    GLOBAL: 'globally',
};

const marketPhrase = (country) => {
    if (!country) return '';
    const phrase =
        MARKET_PHRASES[country.text_id] ||
        (country.name ? `in ${country.name}` : '');
    return phrase ? ` ${phrase}` : '';
};

function Example({ item, catName }) {
    return (
        <>
            Eg. "
            <span
                css={`
                    text-decoration: underline;
                `}
            >
                {item}
            </span>{' '}
            {catName}"
        </>
    );
}

const getPopIndexDataSources = (country) => {
    const uppercaseCountry = country?.toUpperCase() || '';
    return INSTAGRAM_COUNTRIES.includes(uppercaseCountry)
        ? `Google, TikTok, Instagram ${uppercaseCountry}`
        : `Google, TikTok ${uppercaseCountry}`;
};

const copyTemplates = {
    modals: {
        flagModals: {
            suggestTrend: {
                createMessage: (suggestion) => (
                    <div>
                        You suggested <strong>"{suggestion}"</strong>. Our team
                        will review and any change will be reflected in the next
                        data update.
                    </div>
                ),
            },
        },
        watchlistModals: {
            firstItem: {
                createMessage: (itemName) =>
                    `You just added your first trend ${
                        itemName && `, "${itemName}",`
                    } to your watchlist!`,
            },
        },
    },
    views: {
        // EU AI Act Art. 50(1): the panel's line under its composer, and the
        // homepage box's placeholder.
        assistant: {
            disclaimer: 'The assistant is AI.',
            homePlaceholder:
                'Ask Spate AI about trends, brands, ingredients...',
        },
        entityNoData: {
            heading: 'No Data Available',
            /** `advice` closes both messages; the caller decides which one. */
            noCoverage: ({ subject, source, country, advice }) => (
                <>
                    We don&apos;t cover {subject} on {source}
                    {marketPhrase(country)}. {advice}
                </>
            ),
            /** Shared by the empty and not-in-this-taxonomy cases; names no cause. */
            noData: ({ subject, source, country, advice }) => (
                <>
                    We don&apos;t have {source} data for {subject}
                    {marketPhrase(country)} yet. It may not have reached the
                    volume we track, or {source} may organize it differently.{' '}
                    {advice}
                </>
            ),
            tryAnotherSource: 'Try another data source above.',
            /** Where the entity does have data, in place of the generic advice. */
            availability: ({ sources, markets }) => (
                <>
                    You can view it
                    {sources ? <> on {sources}</> : null}
                    {sources && markets ? ' and' : null}
                    {markets ? <> in {markets}</> : null}.
                </>
            ),
        },
        dataSourceTabs: {
            /** Why a tab is inert. Same rule as entityNoData: name no cause. */
            notCovered: ({ source, country }) =>
                `Not available on ${source}${marketPhrase(country)}`,
        },
        /**
         * Empty states for the assistant's inline widgets. Separate from
         * entityNoData because a widget is already titled and has no data-source
         * tabs to send anyone to, so that copy's "try another source above"
         * would be pointing at nothing. Kept together so the two shapes stay in
         * step with each other.
         */
        assistantVisuals: {
            noData: (typeLabel) =>
                `No data available for this ${typeLabel.toLowerCase()}.`,
            noRows: 'No results',
            noTrendLines: "Trend lines aren't available for this data.",
            noLinesSelected: 'No lines selected. Use the list to add some.',
            excludeRow: 'Remove',
            excludeRowLabel: (name) => `Remove ${name} from this widget`,
            excludedCount: (n) => `${n} excluded`,
            excludeTermContains: (value) => `contains “${value}”`,
            excludeMatchLabels: { is: 'Is', contains: 'Contains' },
            excludeMatchTrigger: (value, mode) =>
                `Change how “${value}” matches (currently ${mode})`,
            excludeSpecify: (value) => `Specify: ${value}`,
            excludeHint: (nounPlural) =>
                `Hide ${nounPlural} that match or contain text`,
        },
        categoryTwo: {
            categoryDescription: ({
                name,
                rank,
                parentName,
                derivedGrowth,
                growthRate,
            }) =>
                `${name} ranks #${rank} within the ${parentName} category. This subcategory is ${derivedGrowth} at a ${growthRate} rate compared to the other subcategories.`,
        },
        categoryThree: {
            categoryDescription: ({
                name,
                rank,
                parentName,
                derivedGrowth,
                growthRate,
                topAffinity,
            }) =>
                `${name} ranks #${rank} within the ${parentName} category. This subcategory is ${derivedGrowth} at a ${growthRate} rate compared to the other subcategories. ${
                    topAffinity
                        ? `Based on consumer search intent, this is mainly due to the affinity "${topAffinity}".`
                        : ``
                }`,
            sections: {
                common: {
                    countText: ({
                        categoryName = 'this category',
                        singular,
                        plural,
                        count,
                    }) =>
                        `${count === 1 ? singular : plural} in ${categoryName}`,
                },
            },
        },
    },
    tooltips: {
        industryDefinition: (industryName) =>
            `Hover over images to see how the ${industryName} industry breaks \
            down by consumer search demand. Here we compare the different \
            ${industryName} categories by monthly average search volume and \
            year-over-year growth (past 12 months vs the previous past 12 months). \
            Click through to identify areas of growth and decline by category.`,
        categoryTwoDefinition: (catName) =>
            `We have grouped all the ${catName}-related searches into the following subcategories. You can rank the subcategories by volume, growth or alphabetical order. Click through to see how each subcategory breaks down and compare areas of growth or decline for each.`,
        brandsExample: <Example item="Neutrogena" catName="Face Cream" />,
        affinitiesExample: <Example item="Organic" catName="Face Cream" />,
        affinityBrandsExample: <Example item="Neutrogena" catName="Organic" />,
        predictedGrowth: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )}`,
        },
        predictionConfidence: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(
                    dbDate,
                    NEXT_MONTH_THREE_YEARS_AGO,
                )} to ${d(dbDate)}`,
        },
        industryIndex: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )}`,
        },
        perCapitaIndex: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )}`,
        },
        volume: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )}`,
        },
        brandsVolume: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )}`,
        },
        growth: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )} vs. ${d(dbDate, TWO_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH_LAST_YEAR,
                )}`,
        },
        growthMoM: {
            source: (dbDate, country) =>
                `${country} Search - ${d(dbDate, LAST_MONTH)} vs. ${d(
                    dbDate,
                    LAST_MONTH * 2,
                )}`,
        },
        growthMoMAbsolute: {
            source: (dbDate, country) =>
                `${country} Search - ${d(dbDate, LAST_MONTH)} vs. ${d(
                    dbDate,
                    LAST_MONTH * 2,
                )}`,
        },
        growthQoQ: {
            source: (dbDate, country) =>
                `${country} Search - ${d(dbDate, 3)} - ${d(dbDate, 1)} vs. ${d(dbDate, 6)} - ${d(dbDate, 4)}`,
        },
        growthQoQAbsolute: {
            source: (dbDate, country) =>
                `${country} Search - ${d(dbDate, 3)} - ${d(dbDate, 1)} vs. ${d(dbDate, 6)} - ${d(dbDate, 4)}`,
        },
        growthAbsolute: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )} vs. ${d(dbDate, TWO_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH_LAST_YEAR,
                )}`,
        },
        non_cum_avg_weekly_views: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        non_cum_increase_YoY_rolling: {
            source: (dbDate, country) =>
                `TikTok ${country} - from ${dWeeks(dbDate, 52)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 104)} to ${dWeeks(dbDate, 52)}`,
        },
        non_cum_YoY_rolling: {
            source: (dbDate, country) =>
                `TikTok ${country} - from ${dWeeks(dbDate, 52)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 104)} to ${dWeeks(dbDate, 52)}`,
        },
        non_cum_increase_QoQ_rolling: {
            source: (dbDate, country) =>
                `TikTok ${country} - from ${dWeeks(dbDate, 12)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 24)} to ${dWeeks(dbDate, 12)}`,
        },
        non_cum_QoQ_rolling: {
            source: (dbDate, country) =>
                `TikTok ${country} - from ${dWeeks(dbDate, 12)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 24)} to ${dWeeks(dbDate, 12)}`,
        },
        non_cum_increase_MoM_rolling: {
            source: (dbDate, country) =>
                `TikTok ${country} - from ${dWeeks(dbDate, 4)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 8)} to ${dWeeks(dbDate, 4)}`,
        },
        non_cum_MoM_rolling: {
            source: (dbDate, country) =>
                `TikTok ${country} - from ${dWeeks(dbDate, 4)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 8)} to ${dWeeks(dbDate, 4)}`,
        },
        non_cum_WoW: {
            source: (dbDate, country) =>
                `TikTok ${country} - Week of ${dWeeks(
                    dbDate,
                )} vs. Week of ${dWeeks(dbDate, 1)}`,
        },
        non_cum_increase_WoW: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} vs. ${dWeeks(
                    dbDate,
                    1,
                )}`,
        },
        views: {
            source: (dbDate, country) =>
                `TikTok ${country} - As of ${dWeeks(dbDate)}`,
        },
        total_posts_cnt: {
            source: (dbDate, country) =>
                `TikTok ${country} - As of ${dWeeks(dbDate)}`,
        },
        paid_posts_views_prc: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        paid_posts_views_prc_all: {
            source: (dbDate, country) =>
                `TikTok ${country} - As of ${dWeeks(dbDate)}`,
        },
        partner_paid_partnership_posts_views_prc: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        eligible_for_commission_posts_views_prc: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        eligible_for_commission_posts_views_prc_all: {
            source: (dbDate, country) =>
                `TikTok ${country} - As of ${dWeeks(dbDate)}`,
        },
        views_MoM_increase: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} vs. ${dWeeks(
                    dbDate,
                    4,
                )}`,
        },
        views_MoM: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} vs. ${dWeeks(
                    dbDate,
                    4,
                )}`,
        },
        views_WoW: {
            source: (dbDate, country) =>
                `TikTok ${country} - Week of ${dWeeks(
                    dbDate,
                )} vs. Week of ${dWeeks(dbDate, 1)}`,
        },
        views_WoW_increase: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} vs. ${dWeeks(
                    dbDate,
                    1,
                )}`,
        },
        nb_likes_last_week: {
            source: (dbDate, country) =>
                `TikTok ${country} - Week of ${dWeeks(dbDate)}`,
        },
        nb_likes_all: {
            source: (dbDate, country) =>
                `TikTok ${country} - As of ${dWeeks(dbDate)}`,
        },
        nb_views_last_week: {
            source: (dbDate, country) =>
                `TikTok ${country} - Week of ${dWeeks(dbDate)}`,
        },
        nb_views_all: {
            source: (dbDate, country) =>
                `TikTok ${country} - As of ${dWeeks(dbDate)}`,
        },
        sentiment_score: {
            source: (dbDate, country) =>
                sentimentSource(dbDate, country, 'Sentiment scored by AI'),
        },
        sentimentSummary: {
            source: (dbDate, country) =>
                sentimentSource(dbDate, country, 'Sentiment and summary by AI'),
        },
        averageMonthlyPopularityOneYear: {
            source: (dbDate, country) =>
                `${getPopIndexDataSources(country)} - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )}`,
        },
        googlePopularityYoY: {
            source: (dbDate, country) =>
                `Google ${country} - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )} vs. ${d(dbDate, TWO_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH_LAST_YEAR,
                )}`,
        },
        googlePopularityQoQ: {
            source: (dbDate, country) =>
                `Google ${country} - from ${d(dbDate, 3)} to ${d(
                    dbDate,
                    1,
                )} vs. ${d(dbDate, 6)} to ${d(dbDate, 4)}`,
        },
        googlePopularityMoM: {
            source: (dbDate, country) =>
                `Google ${country} - ${d(dbDate, LAST_MONTH)} vs. ${d(
                    dbDate,
                    LAST_MONTH * 2,
                )}`,
        },
        tikTokPopularityYoY: {
            source: (dbDate, country) =>
                `TikTok ${country} - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )} vs. ${d(dbDate, TWO_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH_LAST_YEAR,
                )}`,
        },
        tikTokPopularityQoQ: {
            source: (dbDate, country) =>
                `TikTok ${country} - from ${d(dbDate, 3)} to ${d(
                    dbDate,
                    1,
                )} vs. ${d(dbDate, 6)} to ${d(dbDate, 4)}`,
        },
        tikTokPopularityMoM: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${d(dbDate, LAST_MONTH)} vs. ${d(
                    dbDate,
                    LAST_MONTH * 2,
                )}`,
        },
        instagramPopularityYoY: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )} vs. ${d(dbDate, TWO_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH_LAST_YEAR,
                )}`,
        },
        instagramPopularityQoQ: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${d(dbDate, 3)} to ${d(
                    dbDate,
                    1,
                )} vs. ${d(dbDate, 6)} to ${d(dbDate, 4)}`,
        },
        instagramPopularityMoM: {
            source: (dbDate, country) =>
                `Instagram ${country} - ${d(dbDate, LAST_MONTH)} vs. ${d(
                    dbDate,
                    LAST_MONTH * 2,
                )}`,
        },
        popularityYoY: {
            source: (dbDate, country) =>
                `${getPopIndexDataSources(country)} - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )} vs. ${d(dbDate, TWO_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH_LAST_YEAR,
                )}`,
        },
        popularityQoQ: {
            source: (dbDate, country) =>
                `Google, TikTok, Instagram ${country} - from ${d(dbDate, 3)} to ${d(
                    dbDate,
                    1,
                )} vs. ${d(dbDate, 6)} to ${d(dbDate, 4)}`,
        },
        popularityMoM: {
            source: (dbDate, country) =>
                `${getPopIndexDataSources(country)} - ${d(dbDate, LAST_MONTH)} vs. ${d(
                    dbDate,
                    LAST_MONTH * 2,
                )}`,
        },
        popularityIncreaseYoY: {
            source: (dbDate, country) =>
                `Google, TikTok, Instagram ${country} - from ${d(dbDate, LAST_YEAR)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )} vs. ${d(dbDate, TWO_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH_LAST_YEAR,
                )}`,
        },
        popularityIncreaseQoQ: {
            source: (dbDate, country) =>
                `${getPopIndexDataSources(country)} - from ${d(dbDate, 3)} to ${d(
                    dbDate,
                    1,
                )} vs. ${d(dbDate, 6)} to ${d(dbDate, 4)}`,
        },
        popularityIncreaseMoM: {
            source: (dbDate, country) =>
                `Google, TikTok, Instagram ${country} - ${d(dbDate, LAST_MONTH)} vs. ${d(
                    dbDate,
                    LAST_MONTH * 2,
                )}`,
        },
        popularityPredictedYoY: {
            source: (dbDate, country) =>
                `${getPopIndexDataSources(country)} - from ${d(dbDate, -12)} to ${d(
                    dbDate,
                    0,
                )} vs. ${d(dbDate, LAST_YEAR)} to ${d(dbDate, LAST_MONTH)}`,
        },
        timeseries: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, THREE_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )}`,
        },
        cluster: {
            source: (dbDate, country) =>
                `${country} Search - from ${d(dbDate, THREE_YEARS_AGO)} to ${d(
                    dbDate,
                    LAST_MONTH,
                )}`,
        },
        clusterPopularity: {
            source: (dbDate, country) =>
                `${getPopIndexDataSources(country)} - from ${d(
                    dbDate,
                    THREE_YEARS_AGO,
                )} to ${d(dbDate, LAST_MONTH)}`,
        },
        clusterTikTok: {
            sourceDataType: 'tiktok',
            source: (dbDate, country) =>
                `TikTok ${country} - from ${dWeeks(dbDate, THREE_YEARS_AGO_WEEKS)} to ${dWeeks(
                    dbDate,
                    0,
                )}`,
        },
        clusterInstagram: {
            sourceDataType: 'instagram',
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(
                    dbDate,
                    THREE_YEARS_AGO_WEEKS,
                )} to ${dWeeks(dbDate, 0)}`,
        },
        totalPosts: {
            source: (dbDate, country) =>
                `Instagram ${country} - As of ${dWeeks(dbDate)}`,
        },
        instagramPostsMoMIncrease: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 4)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 8)} to ${dWeeks(dbDate, 4)}`,
        },
        instagramPostsMoM: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 4)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 8)} to ${dWeeks(dbDate, 4)}`,
        },
        instagramPostsQoQIncrease: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 12)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 24)} to ${dWeeks(dbDate, 12)}`,
        },
        instagramPostsQoQ: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 12)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 24)} to ${dWeeks(dbDate, 12)}`,
        },
        instagramPostsWoWIncrease: {
            source: (dbDate, country) =>
                `Instagram ${country} - Week of ${dWeeks(
                    dbDate,
                )} vs. Week of ${dWeeks(dbDate, 1)}`,
        },
        instagramPostsWoW: {
            source: (dbDate, country) =>
                `Instagram ${country} - Week of ${dWeeks(
                    dbDate,
                )} vs. Week of ${dWeeks(dbDate, 1)}`,
        },
        instagramPostsYoYIncrease: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 52)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 104)} to ${dWeeks(dbDate, 52)}`,
        },
        instagramPostsYoY: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 52)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 104)} to ${dWeeks(dbDate, 52)}`,
        },
        avgWeeklyLikes: {
            source: (dbDate, country) =>
                `Instagram ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        instagramLikesMoMIncrease: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 4)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 8)} to ${dWeeks(dbDate, 4)}`,
        },
        instagramLikesMoM: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 4)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 8)} to ${dWeeks(dbDate, 4)}`,
        },
        instagramLikesQoQIncrease: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 12)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 24)} to ${dWeeks(dbDate, 12)}`,
        },
        instagramLikesQoQ: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 12)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 24)} to ${dWeeks(dbDate, 12)}`,
        },
        instagramLikesWoWIncrease: {
            source: (dbDate, country) =>
                `Instagram ${country} - Week of ${dWeeks(
                    dbDate,
                )} vs. Week of ${dWeeks(dbDate, 1)}`,
        },
        instagramLikesWoW: {
            source: (dbDate, country) =>
                `Instagram ${country} - Week of ${dWeeks(
                    dbDate,
                )} vs. Week of ${dWeeks(dbDate, 1)}`,
        },
        instagramLikesYoYIncrease: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 52)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 104)} to ${dWeeks(dbDate, 52)}`,
        },
        instagramLikesYoY: {
            source: (dbDate, country) =>
                `Instagram ${country} - from ${dWeeks(dbDate, 52)} to ${dWeeks(dbDate)} vs. ${dWeeks(dbDate, 104)} to ${dWeeks(dbDate, 52)}`,
        },
        searchQueries: {
            source: (dbDate, country) => (
                <div>
                    <div
                        css={`
                            font-size: 0.875rem;
                            display: inline-flex;
                            align-items: center;
                            justify-content: flex-start;
                        `}
                    >
                        Data provided by
                        <span
                            css={`
                                display: flex;
                                align-items: center;
                            `}
                        >
                            <SiSemrush css="font-size: 1.25em; margin: 0 .5ch; color: white;" />
                            Semrush
                        </span>
                    </div>
                    <div>
                        {`${country} Search - from ${d(dbDate, LAST_YEAR)} to ${d(
                            dbDate,
                            LAST_MONTH,
                        )}`}
                    </div>
                </div>
            ),
        },
        hashtags: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        relatedKeywords: {
            source: (dbDate, country) =>
                `TikTok ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        relatedTerms: {
            source: (dbDate, country) =>
                `TikTok ${country} - As of ${dWeeks(dbDate)}`,
        },
        instagramRelatedKeywords: {
            source: (dbDate, country) =>
                `Instagram ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        instagramRelatedTerms: {
            source: (dbDate, country) =>
                `Instagram ${country} - As of ${dWeeks(dbDate)}`,
        },
        instagramRelatedHashtags: {
            source: (dbDate, country) =>
                `Instagram ${country} - ${dWeeks(dbDate)} - ${dWeeks(dbDate, 4)}`,
        },
        topPosts: {
            source: (dbDate, country) =>
                `TikTok ${country} - posts from ${dWeeks(
                    dbDate,
                    8,
                )} to ${dWeeks(dbDate)}`,
        },
        'last week views': {
            source: (dbDate, country) =>
                `TikTok ${country} - post views from ${dWeeks(
                    dbDate,
                    1,
                )} to ${dWeeks(dbDate)}`,
        },
        'total post views': {
            source: (dbDate, country) =>
                `TikTok ${country} - post views as of ${dWeeks(dbDate)}`,
        },
        'avg weekly views': {
            source: (dbDate, country) =>
                `TikTok ${country} - avg weekly post views as of ${dWeeks(dbDate)}`,
        },
        engagement_prc: {
            source: (dbDate, country) =>
                `TikTok ${country} - total post likes vs. views as of ${dWeeks(dbDate)}`,
        },
        'instagram last week likes': {
            source: (dbDate, country) =>
                `Instagram ${country} - post likes from ${dWeeks(
                    dbDate,
                    1,
                )} to ${dWeeks(dbDate)}`,
        },
        'instagram total post likes': {
            source: (dbDate, country) =>
                `Instagram ${country} - post likes as of ${dWeeks(dbDate)}`,
        },
        'instagram avg weekly likes': {
            source: (dbDate, country) =>
                `Instagram ${country} - avg weekly post likes as of ${dWeeks(dbDate)}`,
        },
    },
};

export default copyTemplates;
